import { useTranslation } from "react-i18next"
import { Box, Stack } from "@mui/material"
import Typography from "@mui/material/Typography"
import fp from "lodash/fp"

import type { Powerline } from "types/powerlines.types"

import PageTitle from "widgets/common/PageTitle"
import EntityTag from "widgets/common/EntityTag"
import { customValueFormatter } from "helpers/formatters/plots/plots"
import { formatCurrentSeasonalAmpacity } from "helpers/utils/seasonalAmpacity"
import { nameOrUnnamed } from "helpers/utils/translations"

interface PowerlineSummaryProps {
  powerline: Powerline
}

const PowerlineSummary = ({ powerline }: PowerlineSummaryProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  // Seasonal ampacity is summarized as the value in effect today plus the date
  // window of the current season (full breakdown lives in the popup).
  const seasonal = formatCurrentSeasonalAmpacity(powerline.ampacity_seasonal, locale)

  return (
    <Box
      sx={{
        marginBottom: "1rem",
      }}
      data-testid="powerline-summary"
    >
      <Stack
        flexDirection="row"
        flexWrap="wrap"
        alignItems="center"
        columnGap={2}
        rowGap={1}
        marginBottom={1.5}
      >
        <EntityTag entity="powerline" />
        <PageTitle
          text={nameOrUnnamed(powerline.name, t)}
          styles={{ fontStyle: powerline.name ? "normal" : "italic" }}
        />
      </Stack>
      <Stack gap={1.5}>
        <Stack
          flexDirection="row"
          flexWrap="wrap"
          rowGap={0.5}
          columnGap={2}
          color="text.secondary"
        >
          <Typography>ID: {powerline.id}</Typography>
          {!fp.isNil(powerline.voltage_nominal) && (
            <Typography>
              {t("powerlines.VOLTAGE_NOMINAL")}:{" "}
              {customValueFormatter({
                suffix: "V",
                decimalPlaces: 0,
                formatUnits: true,
                locale,
              })(powerline.voltage_nominal)}
            </Typography>
          )}
          {seasonal ? (
            <Typography>
              {t("powerlines.AMPACITY_NOMINAL")}: {seasonal.label}
            </Typography>
          ) : (
            !fp.isNil(powerline.ampacity_nominal) && (
              <Typography>
                {t("powerlines.AMPACITY_NOMINAL")}:{" "}
                {customValueFormatter({
                  suffix: "A",
                  decimalPlaces: 0,
                  formatUnits: true,
                  locale,
                })(powerline.ampacity_nominal)}
              </Typography>
            )
          )}
          {!fp.isNil(powerline.length) && (
            <Typography>
              {t("powerlines.LENGTH")}:{" "}
              {customValueFormatter({
                suffix: "m",
                decimalPlaces: 2,
                formatUnits: true,
                locale,
              })(powerline.length)}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}

export default PowerlineSummary
